import React, { useCallback, useState } from 'react'
import { flattenStateValue } from './utils/flattenStateValue'
import { MultiStepPageLayout } from './MultiStepPageLayout/MultiStepPageLayout'
import { Interpreter, State } from 'xstate'
import { flushSync } from 'react-dom'

type MultiStepFlowProps = {
  current: State<any, any>
  send: Interpreter<any, any, any>['send']
  stateToComponentKey: Record<string, string>
  componentMap: Record<string, React.FC<any>>
  stateSteps: Record<string, number>
  getExtraStepProps?: (componentKey: string) => Record<string, any>
  onSkip?: () => void
  totalSteps: number
  showBackButton?: boolean
  currentStep?: number
  siderImage?: string
  siderComponent?: React.FC<any>
  progressPercentage?: number
  fullWidth?: boolean
  fullScreen?: boolean
  backgroundClassName?: string
  showMobileLogo?: boolean
  customBack?: () => void
}

const MultiStepFlow: React.FC<MultiStepFlowProps> = ({
  current,
  send,
  stateToComponentKey,
  componentMap,
  stateSteps,
  getExtraStepProps,
  onSkip,
  totalSteps,
  showBackButton = true,
  currentStep: customCurrentStep,
  siderImage,
  siderComponent,
  progressPercentage,
  fullWidth,
  fullScreen,
  backgroundClassName,
  showMobileLogo = false,
  customBack,
}) => {
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')

  const flatState = flattenStateValue(current.value)
  const componentKey = stateToComponentKey[flatState]
  const StepComponent = componentMap[componentKey]

  const currentStep = customCurrentStep || stateSteps[componentKey] || 1

  const next = useCallback(
    (event?: { type: string; goal?: string }) => {
      flushSync(() => {
        setDirection('forward')
      })
      if (event) {
        send(event)
      } else {
        send({ type: 'NEXT' })
      }
    },
    [send],
  )

  const back = useCallback(() => {
    flushSync(() => {
      setDirection('backward')
    })
    if (customBack) {
      customBack()
    } else {
      send({ type: 'BACK' })
    }
  }, [customBack, send])

  const extraProps = getExtraStepProps ? getExtraStepProps(componentKey) : {}

  return (
    <MultiStepPageLayout
      currentStep={currentStep}
      totalSteps={totalSteps}
      onBack={back}
      siderImage={siderImage}
      siderComponent={siderComponent}
      onSkip={onSkip}
      currentStepKey={componentKey}
      showBackButton={showBackButton}
      direction={direction}
      progressPercentage={progressPercentage}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      backgroundClassName={backgroundClassName}
      showMobileLogo={showMobileLogo}
    >
      {StepComponent && (
        <StepComponent
          onNext={next}
          onSkip={onSkip}
          onBack={back}
          {...extraProps}
          send={send}
          context={current.context}
        />
      )}
    </MultiStepPageLayout>
  )
}

export default MultiStepFlow
